/*******************************************************************
* PAGE        : apiCaller
* REMARKS     : Comman api Caller
* CREATED ON  : 19-12-2025
* AUTHOR      : Bamroliya.Paras
* MODIFY      : Bamroliya.Paras
* MODIFY ON   : 24-02-2026
********************************************************************/

import localDataStorage from '../store/localDataStorage';
import { URL, AIURL } from './URL';
import CryptoJS from "crypto-js";
import axios from "axios";

const globalUrl = URL();
const aiGlobalUrl = AIURL();

const getAuthToken = () => {
    // get form local storage('authToken');
    return localDataStorage.getLocalStorage('accessToken');
};

const encodeParams = (params) => {
    return Object.keys(params)
        .map(key => encodeURIComponent(key) + '=' + encodeURIComponent(params[key]))
        .join('&');
};

const aesKey = "1234567890abcdef1234567890abcdef";

const decryptAES = (encryptedBase64, key) => {

    // Convert key to UTF8 WordArray
    const keyUtf8 = CryptoJS.enc.Utf8.parse(key);

    // Extract IV → first 16 bytes (128 bits)
    const iv = CryptoJS.enc.Utf8.parse(key.substring(0, 16));

    // Decode Base64
    const cipherText = CryptoJS.enc.Base64.parse(encryptedBase64);

    // Decrypt
    const decrypted = CryptoJS.AES.decrypt(
        { ciphertext: cipherText },
        keyUtf8,
        { iv: iv, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 }
    );

    return decrypted.toString(CryptoJS.enc.Utf8);
};

// Same base64-check + decrypt + JSON.parse logic apiCaller used inline, shared with the refresh-token flow
const parseApiResponse = (responseText) => {
    // Check if the response is a base64 string (likely encrypted)

    if (typeof responseText !== "string") {
        return responseText;
    }

    const trimmed = responseText.trim();

    // 1. Check if response is XML
    if (trimmed.startsWith("<?xml") || trimmed.startsWith("<")) {
        const match = trimmed.match(/<string[^>]*>([\s\S]*?)<\/string>/);

        if (match) {
            const xmlValue = match[1];

            // Decode XML entities such as &amp;
            const decodedValue = xmlValue
                .replace(/&amp;/g, "&")
                .replace(/&lt;/g, "<")
                .replace(/&gt;/g, ">")
                .replace(/&quot;/g, '"')
                .replace(/&#39;/g, "'");

            // The value inside <string> is JSON
            try {
                return JSON.parse(decodedValue);
            } catch {
                return decodedValue;
            }
        }

        return trimmed;
    }

    if (responseText.match(/^[A-Za-z0-9+/=]+$/)) {
        const decryptedData = decryptAES(responseText, aesKey);
        if (responseText === 'null') {
            return decryptedData;
        }
        return JSON.parse(decryptedData); // Assuming the decrypted data is in JSON format
    }

    // If response is not base64 (not encrypted), attempt to parse as JSON
    return JSON.parse(responseText);
};

// Dedicated instance for the .Net API so we can attach auth + 401 refresh interceptors
const apiInstance = axios.create({
    baseURL: globalUrl,
    transformResponse: [(data) => data], // keep raw text so parseApiResponse's base64/decryptAES check still works
});

apiInstance.interceptors.request.use((config) => {
    const token = getAuthToken();
    const isFormData = config.data instanceof FormData;
    config.headers = {
        ...config.headers,
        ...(!isFormData && { "Content-Type": "application/x-www-form-urlencoded" }), // let axios set the multipart boundary itself for FormData
        ...(token && { 'Authorization': `Bearer ${token}` }), // Add token to header if available
    };
    return config;
});

const REFRESH_TOKEN_API_URL = 'CMExpressAPIAuthenticationUser.asmx/CMExpressAPIRefreshToken';

const getRefreshToken = () => localDataStorage.getLocalStorage('refreshToken');
const getCompanyGroupId = () => localDataStorage.getLocalStorage('companyGroupId');

const saveTokens = (accessToken, refreshToken) => {
    localDataStorage.setLocalStorage('accessToken', accessToken);
    if (refreshToken) {
        localDataStorage.setLocalStorage('refreshToken', refreshToken);
    }
};

// Separate instance for the refresh/logout calls themselves, so they never re-enter apiInstance's 401 handling
const refreshInstance = axios.create({
    baseURL: globalUrl,
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    transformResponse: [(data) => data],
});

// Refresh token itself is invalid/expired — clear the session (localStorage + the persisted
// redux-persist state) and bounce to /login, without calling the logout API (there's no valid
// token left to authorize that call). isAuthenticated defaults to false in reduxMasterSlice, so
// dropping the persisted state is enough to make the reload land on the login route.
const clearSessionAndRedirect = () => {
    localDataStorage.removeLocalStorage();
    sessionStorage.removeItem('persist:root');
    window.location.href = '/login';
};

let isRefreshing = false;
let pendingRequests = [];

const onTokenRefreshed = (newToken) => {
    pendingRequests.forEach((cb) => cb(newToken));
    pendingRequests = [];
};

apiInstance.interceptors.response.use((response) => response,
    async (error) => {
        const originalRequest = error.config;

        if (error.response?.status !== 401 || !originalRequest || originalRequest._retry) {
            return Promise.reject(error);
        }

        originalRequest._retry = true;

        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                pendingRequests.push((newToken) => {
                    if (!newToken) {
                        reject(error);
                        return;
                    }
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;
                    resolve(apiInstance(originalRequest));
                });
            });
        }

        isRefreshing = true;
        try {
            const refreshToken = getRefreshToken();

            if (!refreshToken) {
                throw new Error('Refresh token not found');
            }

            const refreshResponse = await refreshInstance({
                url: REFRESH_TOKEN_API_URL,
                method: 'POST',
                data: encodeParams({ refreshToken: refreshToken, strCompanyGroupId: getCompanyGroupId() }),
            });

            const parsed = parseApiResponse(refreshResponse.data);

            if (parsed?.status === false) {
                throw new Error(parsed?.message || 'Refresh token is invalid or expired');
            }

            const newAccessToken = parsed?.data?.[0]?.AccessToken;
            const newRefreshToken = parsed?.data?.[0]?.RefreshToken;

            if (!newAccessToken) {
                throw new Error('Refresh API did not return AccessToken');
            }

            saveTokens(newAccessToken, newRefreshToken);
            onTokenRefreshed(newAccessToken);

            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            return apiInstance(originalRequest);
        } catch (refreshError) {
            onTokenRefreshed(null);
            clearSessionAndRedirect();
            return Promise.reject(refreshError);
        } finally {
            isRefreshing = false;
        }
    }
);

//#region .Net API
export const apiCaller = async ({ apiUrl, method = 'POST', body = null, headers = {} }) => {

    try {
        const response = await apiInstance({
            url: apiUrl,
            method: method,
            headers: headers,
            data: body ? encodeParams(body) : null,
        });
        return parseApiResponse(response.data);

    } catch (error) {
        // throw error;
    }
};
//#endregion

//#region AI API
export const aiApiCaller = async ({ apiUrl, method = 'POST', body = null, headers = {} }) => {

    const token = getAuthToken();
    const url = aiGlobalUrl + apiUrl

    const defaultHeaders = {
        ...headers,
        "Content-Type": "application/x-www-form-urlencoded",
        // ...(token && { 'Authorization': `Bearer ${token}` }) // Add token to header if available
    };

    try {
        const response = await axios({
            url: url,
            method: method,
            headers: defaultHeaders,
            data: body ? encodeParams(body) : null,
            transformResponse: [(data) => data], // keep raw text, matching original response.text()
        });

        const responseText = response.data;

        // If response is not base64 (not encrypted), attempt to parse as JSON
        try {
            return JSON.parse(responseText); // Attempt to parse as JSON
        } catch (jsonError) {
            throw jsonError;
        }

    } catch (error) {
        // throw error;
    }
}
//#endregion

//#region .Net file Upload API
export const fileUploadAPI = async ({ apiUrl, method = 'POST', body = null }) => {
    try {
        const response = await apiInstance({
            url: apiUrl,
            method: method,
            data: body ? body : null,
        });

        return response;

    } catch (error) {
        // throw error;
    }
}
//#endregion
